import React, { useCallback } from 'react';
import Bullet from './Bullet';
import { normalizeItem, isNonVeg } from '../utils/menuHelpers';
import { useSquareLiveItem } from '../hooks/useSquareLive';

/**
 * A single interactive menu item row.
 * Shows bullet + name. On hover shows tooltip (desktop), on tap opens modal
 * (mobile).
 *
 * The published price wins over Square's live price. Square used to override it
 * here, which made this row disagree with the mobile list (which has always
 * rendered the published price) and made price edits in the menu editor look
 * like they hadn't saved. Sold-out state still comes from Square — that's
 * stock, not money, and nobody maintains it by hand.
 */
const MenuItem = ({ item, onSelect, forceNonvegCheck = false }) => {
  const { name, desc, img, price } = normalizeItem(item);
  const nonveg = forceNonvegCheck && isNonVeg(name);
  const live = useSquareLiveItem(name);
  // Fall back to Square only when the curated menu carries no price at all.
  const displayPrice = price ?? (live?.priceCents != null ? live.priceCents / 100 : null);
  const soldOut = live?.soldOut === true;

  const handleClick = useCallback(() => {
    onSelect?.({ name, desc, img });
  }, [name, desc, img, onSelect]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  return (
    <div
      className={`fm-item${soldOut ? ' fm-item--soldout' : ''}`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Bullet type={nonveg ? 'nonveg' : 'veg'} />
      <span className="fm-item__name">{name}</span>
      {soldOut && <span className="fm-item__soldout">Sold out</span>}
      {displayPrice != null && (
        <span className="fm-item__price">${displayPrice.toFixed(2)}</span>
      )}
      {desc && (
        <span className="fm-item__tooltip" aria-hidden="true">
          {desc}
        </span>
      )}
    </div>
  );
};

export default React.memo(MenuItem);
